import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Shield, 
  LayoutDashboard, 
  Database, 
  Zap, 
  Star, 
  Activity, 
  LogOut, 
  Sparkles, 
  Menu, 
  X, 
  ChevronRight 
} from 'lucide-react';
import SEOHead from '../../components/SEOHead';
import AdminLogin from './AdminLogin';
import AdminOverview from './AdminOverview';
import AdminDataExplorer from './AdminDataExplorer';
import AdminFunctionTriggers from './AdminFunctionTriggers';
import AdminTestimonials from '../AdminTestimonials';
import AdminAgentUsage from '../agents/AdminAgentUsage';

export default function AdminLayout() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [initialExplorerCol, setInitialExplorerCol] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem('admin_authenticated');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_authenticated');
    sessionStorage.removeItem('admin_password');
    setIsAuthenticated(false);
  };

  const handleNavigateTab = (tabId, collectionName) => {
    if (collectionName) setInitialExplorerCol(collectionName);
    setActiveTab(tabId);
    setMobileMenuOpen(false);
  };

  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  const navItems = [
    { id: 'overview', label: 'System Overview', icon: LayoutDashboard },
    { id: 'explorer', label: 'Data Explorer (CRUD)', icon: Database },
    { id: 'triggers', label: 'Cloud Function Triggers', icon: Zap },
    { id: 'testimonials', label: 'Testimonials Manager', icon: Star },
    { id: 'analytics', label: 'Usage Analytics', icon: Activity }
  ];

  return (
    <div className="min-h-screen bg-[#070E1B] text-white flex flex-col font-opensans">
      <SEOHead title="Master Admin Dashboard | Grow & Stay Rich" />

      {/* Top Navigation Bar */}
      <header className="bg-[#0D1B2A] border-b border-[#C9A84C]/30 sticky top-0 z-40 shadow-xl backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-18 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src="/logo-horizontal-white.svg"
                alt="Grow & Stay Rich Logo"
                className="h-9 sm:h-10 w-auto transition-transform group-hover:scale-105"
              />
              <div className="hidden sm:block border-l border-[#C9A84C]/30 pl-3">
                <span className="text-[10px] text-[#E8D4A0] uppercase tracking-widest font-semibold block">Master Admin Portal</span>
              </div>
            </Link>
          </div>

          {/* Desktop Right Header Tools */}
          <div className="hidden md:flex items-center gap-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs font-semibold text-[#E8D4A0]">
              <Sparkles className="w-3.5 h-3.5 text-[#C9A84C]" />
              Super Admin Session Active
            </div>

            <button
              onClick={handleLogout}
              className="bg-[#162236] hover:bg-red-950/80 text-slate-300 hover:text-red-400 border border-slate-700 hover:border-red-800 px-3.5 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>

          {/* Mobile Menu Trigger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-slate-300 hover:text-white p-2"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Main Container */}
      <div className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col md:flex-row gap-8">
        {/* Desktop Sidebar Navigation */}
        <aside className="hidden md:block w-64 shrink-0 space-y-2">
          <div className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 rounded-2xl p-3 shadow-xl space-y-1">
            {navItems.map((item) => {
              const IconComponent = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigateTab(item.id)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-[#C9A84C] to-[#8C6D23] text-[#070E1B] font-bold shadow-lg'
                      : 'text-slate-300 hover:bg-[#162236] hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <IconComponent className="w-4 h-4" />
                    <span>{item.label}</span>
                  </div>
                  {isActive && <ChevronRight className="w-4 h-4" />}
                </button>
              );
            })}
          </div>
        </aside>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0D1B2A] border border-[#C9A84C]/30 rounded-2xl p-4 space-y-2 mb-4">
            {navItems.map((item) => {
              const IconComponent = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigateTab(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                    isActive
                      ? 'bg-[#C9A84C] text-[#070E1B] font-bold'
                      : 'text-slate-300 bg-[#162236]'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span>{item.label}</span>
                </button>
              );
            })}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-red-400 bg-red-950/40 border border-red-900/50 mt-4"
            >
              <LogOut className="w-4 h-4" />
              Logout Session
            </button>
          </div>
        )}

        {/* Main Content Area */}
        <main className="flex-1 min-w-0">
          {activeTab === 'overview' && (
            <AdminOverview onNavigateTab={handleNavigateTab} />
          )}

          {activeTab === 'explorer' && (
            <AdminDataExplorer initialCollection={initialExplorerCol} />
          )}

          {activeTab === 'triggers' && (
            <AdminFunctionTriggers />
          )}

          {activeTab === 'testimonials' && (
            <AdminTestimonials />
          )}

          {activeTab === 'analytics' && (
            <AdminAgentUsage />
          )}
        </main>
      </div>
    </div>
  );
}
